import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Loader } from 'semantic-ui-react';
import { RawLogo, DNAAlphabet } from 'logojs-react';
import { associateBy } from 'queryz';
import { GraphDataset } from './types';
import { Graph } from 'components/aggregate/graphs';
import { downloadSVG } from 'components/tf/geneexpression/utils';

DNAAlphabet[0].color = "#228b22";
DNAAlphabet[3].color = "red";

const ATACPlot: React.FC<{ name: string; accession: string; pwm: number[][] }> = ({ name, accession, pwm }) => {

    const [ data, setData ] = useState<any | null>(null);
    const [ loading, setLoading ] = useState(true);
    useEffect( () => {
        setLoading(true);
        setData(null);
        fetch(`https://screen-beta-api.wenglab.org/dnase_aggregate/motif-aggregate/${accession}.DNase-aggregate.json.gz`)
            .then(x => x.json())
            .then(x => { setData(x); setLoading(false) });
    }, [ accession, name ]);

    const [limit, setLimit] = useState(1000);

    const ref = useRef<SVGSVGElement>(null);
    const setLimitS = useCallback((limit: number) => {
        if (limit < 10) limit = 10;
        if (limit > 1000) limit = 1000;
        setLimit(limit);
    }, []);

    const datasets = useMemo(() => {
        if (!data) return;
        const r: GraphDataset[] = [];
        if (data)
            r.push({
                reverse_values: data[name]?.reverse,
                forward_values: data[name]?.forward,
                title: 'DNase-seq',
                color: '#00aa00',
            });
        return r;
    }, [data]);

    const options = useMemo(
        () =>
            datasets?.map(dataset => ({
                key: dataset.title,
                text: 'Footprinting: ' + dataset.title,
                value: dataset.title,
                content: dataset.title,
            })),
        [datasets]
    );
    useEffect(() => setDataset(datasets && datasets[0]), [datasets]);

    const datasetMap = useMemo(
        () =>
            associateBy(
                datasets || [],
                x => x.title,
                x => x
            ),
        [datasets]
    );
    const [dataset, setDataset] = useState(datasets && datasets[0]);

    const max = useMemo(
        () =>
            Math.max(
                ...(dataset?.forward_values?.slice(1000 - limit, 1000 + limit) || []),
                ...(dataset?.reverse_values?.slice(1000 - limit, 1000 + limit) || [])
            ),
        [dataset, limit]
    );

    if (loading || !data || !datasets) return <Loader active>Loading...</Loader>;
    if (!dataset || !data[name]) return null;

    return (
        <>
            {options && (
                <Dropdown
                    as="h4"
                    style={{ textAlign: 'center', marginBottom: '-1em' }}
                    options={options}
                    onChange={(_, v) => {
                        if (!v.value) return;
                        setDataset(datasetMap.get(v.value as string)!);
                    }}
                    value={dataset.title}
                />
            )}
            <Graph
                is_forward_reverse={true}
                proximal_values={dataset.forward_values.slice(1000 - limit, 1000 + limit)}
                distal_values={(dataset.reverse_values || dataset.forward_values).slice(1000 - limit, 1000 + limit)}
                limit={limit}
                xlabel="distance from motif (bp)"
                ylabel="read count"
                height={220}
                padBottom
                dataset={{ target: 'ATAC', accession }}
                yMax={max * 1.2}
                hideTitle
                sref={ref}
            >
                <g x={-Math.floor(pwm.length / 2)} y={-max / 6} transform={`scale(1,${max / 3 / 200})`}>
                    <RawLogo values={pwm} alphabet={DNAAlphabet} x={0} y={0} glyphWidth={1} stackHeight={100} />
                </g>
            </Graph>
            <Button
                onClick={() => downloadSVG(ref, "footprint.svg")}
            >
                Export SVG
            </Button>
            <Button onClick={() => setLimitS(Math.floor(limit / 2))}>+</Button>
            <Button onClick={() => setLimitS(Math.floor(limit * 2))}>-</Button>
        </>
    );
};
export default ATACPlot;
